import { createReducer } from '@reduxjs/toolkit';
import {
    listVehiclesRequest,
    listVehiclesSuccess,
    listVehiclesFailure,
    listVehiclesDeliveryRequest,
    listVehiclesDeliverySuccess,
    listVehiclesDeliveryFailure,
    setVehicleRequest,
    getVehicleRequest,
    getVehicleSuccess,
    getVehicleFailure,
    updateVehicleRequest,
    updateVehicleSuccess,
    updateVehicleFailure,
    resetErrorRequest,
    getMediaTypesRequest,
    getMediaTypesSuccess,
    getMediaTypesFailure,
    uploadMediaRequest,
    uploadMediaSuccess,
    uploadMediaFailure,
    uploadDocumentRequest,
    uploadDocumentSuccess,
    uploadDocumentFailure,
    getInspectionsRequest,
    getInspectionsSuccess,
    getInspectionsFailure,
    InspectionSection,
    MediaType,
    Vehicle,
    Store,
    getStoreLocationsRequest,
    getStoreLocationsSuccess,
    getStoreLocationsFailure,
} from './actions';

export interface VehicleState {
    [key: string]: any;
    mediatypes: MediaType[];
    inspections: InspectionSection[];
    vehicles: Vehicle[];
    vehicle: Vehicle | null;
    deliveries: Vehicle[];
    purchase_inventory: Vehicle[];
    loading: boolean;
    error: string | null;
    stores: Store[];
}

const initialState: VehicleState = {
    mediatypes: [],
    deliveries: [],
    purchase_inventory: [],
    inspections: [],
    vehicles: [],
    vehicle: null,
    stores: [],
    loading: false,
    error: null,
};

const vehicleReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(resetErrorRequest, (state) => {
            state.error = null;
        })
        .addCase(listVehiclesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(listVehiclesSuccess, (state, action) => {
            state[action.payload.save_as || 'vehicles'] = action.payload.vehicles;
            state.loading = false;
        })
        .addCase(listVehiclesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(setVehicleRequest, (state, action) => {
            state.vehicle = action.payload;
            state.error = null;
        })
        .addCase(getVehicleRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getVehicleSuccess, (state, action) => {
            state.vehicle = action.payload; // Set vehicles from the server
            state.loading = false;
        })
        .addCase(getVehicleFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getMediaTypesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getMediaTypesSuccess, (state, action) => {
            state.loading = false;
            state.mediatypes = action.payload;
        })
        .addCase(getMediaTypesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(updateVehicleRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateVehicleSuccess, (state, action) => {
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
            state.deliveries = state.deliveries.filter((el) => el.id != action.payload.id);
            if (state.vehicle?.id == action.payload.id) {
                state.vehicle = {
                    ...state.vehicle,
                    ...action.payload,
                };
            }
            state.loading = false;
        })
        .addCase(updateVehicleFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(listVehiclesDeliveryRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(listVehiclesDeliverySuccess, (state, action) => {
            state.loading = false;
            state.deliveries = action.payload;
        })
        .addCase(listVehiclesDeliveryFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadMediaRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(uploadMediaSuccess, (state, action) => {
            state.loading = false;
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    medias: [...(state.vehicle.medias || []), ...action.payload.medias],
                };
            }
        })
        .addCase(uploadMediaFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadDocumentRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(uploadDocumentSuccess, (state, action) => {
            state.loading = false;
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    documents: [...(state.vehicle.documents || []), ...action.payload.documents],
                };
            }
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
        })
        .addCase(uploadDocumentFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getInspectionsRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getInspectionsSuccess, (state, action) => {
            state.loading = false;
            state.inspections = action.payload;
        })
        .addCase(getInspectionsFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getStoreLocationsRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getStoreLocationsSuccess, (state, action) => {
            state.loading = false;
            state.stores = action.payload;
        })
        .addCase(getStoreLocationsFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
});

export default vehicleReducer;
